import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';

import { usePropertyStore } from '@/stores/property-store';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { useAuthStore } from '@/stores/auth-store';
import { UpsellSheet } from '@/components/common/UpsellSheet';
import { UPSELL_COPY } from '@/constants/upsell-copy';
import { PropertyForm } from '@/components/property/PropertyForm';
import type { CreatePropertyInput } from '@/types/property';

export function PropertyFormPage() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = Boolean(propertyId);

  const { getProperty, addProperty, editProperty, properties, loadProperties } = usePropertyStore();
  const isPro = useUserProfileStore((s) => s.isPro);
  const { user } = useAuthStore();

  const [initialValues, setInitialValues] = useState<Parameters<typeof PropertyForm>[0]['initialValues']>(undefined);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showPropertyGate, setShowPropertyGate] = useState(false);

  useEffect(() => {
    if (properties.length === 0) void loadProperties();
  }, [properties.length, loadProperties]);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- loading flag set before async fetch; reset in cleanup
    setIsLoading(true);
    getProperty(propertyId)
      .then((property) => {
        if (cancelled) return;
        if (!property) {
          setLoadError('Property not found.');
        } else {
          setInitialValues(property);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError('Failed to load property.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [propertyId, getProperty]);

  async function handleSubmit(data: CreatePropertyInput) {
    // Gate: Starter users can only have 1 property
    if (!isEditMode && !isPro() && properties.length >= 1) {
      setShowPropertyGate(true);
      return;
    }

    const fromOnboarding = (location.state as { fromOnboarding?: boolean } | null)?.fromOnboarding === true;

    setIsSubmitting(true);
    try {
      if (isEditMode && propertyId) {
        await editProperty(propertyId, data);
        void navigate(`/properties/${propertyId}`, { replace: true });
      } else {
        const created = await addProperty(data);
        if (fromOnboarding) {
          void navigate('/', { replace: true, state: { toastMessage: 'Property mapped. Conditions online.' } });
        } else {
          void navigate(`/properties/${created.id}`, { replace: true });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    if (isEditMode && propertyId) {
      void navigate(`/properties/${propertyId}`);
    } else {
      void navigate('/properties');
    }
  }

  const backHref = isEditMode && propertyId ? `/properties/${propertyId}` : '/properties';
  const backLabel = isEditMode ? '← Property' : '← Properties';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-body-sm text-[var(--color-text-muted)]">Loading…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-body-sm text-[var(--color-text-secondary)]">{loadError}</p>
        <Link to="/properties" className="text-body-sm text-[var(--color-accent)] hover:opacity-80 transition-opacity">
          ← Back to Properties
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <div className="flex flex-col gap-1">
        <Link
          to={backHref}
          className="text-body-sm text-[var(--color-accent)] hover:opacity-80 transition-opacity"
        >
          {backLabel}
        </Link>
        <h1 className="font-display text-display-lg text-[var(--color-text-display)] mt-1">
          {isEditMode ? 'Edit Property' : 'New Property'}
        </h1>
      </div>

      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-[var(--shadow-card)] p-4 sm:p-6">
        <PropertyForm
          initialValues={initialValues}
          defaultState={user?.defaultState ?? ''}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </section>

      {showPropertyGate && (
        <UpsellSheet
          featureName={UPSELL_COPY.second_property.featureName}
          description={UPSELL_COPY.second_property.description}
          featureGate="second_property"
          onDismiss={() => setShowPropertyGate(false)}
        />
      )}
    </div>
  );
}
